import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import EventIcon from '@mui/icons-material/Event';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { IMyIssueSummary } from '@pulse/shared';
import { alpha, Box, Grid, ICON_SIZE, Paper, Stack, Typography, useTheme } from '@pulse/ui';
import type { ReactNode } from 'react';

function todayIso(): string {
  return new Date().toLocaleDateString('en-CA');
}

// Title top-left, icon badge top-right, big colored number, caption below — matches the
// requirements/ui/home page design's Overdue/Due Today/Hours Logged card shape.
function StatCard({
  title,
  icon,
  iconColor,
  value,
  valueColor,
  caption,
}: {
  title: string;
  icon: ReactNode;
  iconColor: string;
  value: number;
  valueColor: string;
  caption: string;
}) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 3, height: '100%' }}>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="body2" color="text.secondary" fontWeight={600}>{title}</Typography>
          <Box sx={{ width: 27, height: 27, borderRadius: 1.5, bgcolor: alpha(iconColor, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {icon}
          </Box>
        </Stack>
        <Typography sx={{ fontSize: 34, fontWeight: 900, lineHeight: 1 }} color={valueColor}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{caption}</Typography>
      </Stack>
    </Paper>
  );
}

export function KpiRow({
  issues,
  openRisksCount,
  openDependenciesCount,
  openEscalationsCount,
}: {
  issues: IMyIssueSummary[];
  openRisksCount: number;
  openDependenciesCount: number;
  openEscalationsCount: number;
}) {
  const theme = useTheme();
  const today = todayIso();

  const activeIssues = issues.filter((i) => i.status !== 'Done');
  const overdueCount = activeIssues.filter((i) => i.dueDate && i.dueDate < today).length;
  const dueTodayCount = activeIssues.filter((i) => i.dueDate === today).length;
  const expeditedCount = activeIssues.filter((i) => i.expedited).length;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Expedite"
          icon={<BoltIcon sx={{ color: 'warning.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.warning.main}
          value={expeditedCount}
          valueColor="primary.main"
          caption="Marked top priority"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Overdue Tasks"
          icon={<WarningAmberIcon sx={{ color: 'error.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.error.main}
          value={overdueCount}
          valueColor="error.main"
          caption="Need attention now"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Due Today"
          icon={<EventIcon sx={{ color: 'warning.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.warning.main}
          value={dueTodayCount}
          valueColor="primary.main"
          caption={`of ${activeIssues.length} assigned to you`}
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Open Risks"
          icon={<WarningAmberIcon sx={{ color: 'warning.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.warning.main}
          value={openRisksCount}
          valueColor="warning.main"
          caption="Owned by you, not yet resolved"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Open Dependencies"
          icon={<ArrowRightAltIcon sx={{ color: 'info.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.info.main}
          value={openDependenciesCount}
          valueColor="info.main"
          caption="Owned by you, not yet resolved"
        />
      </Grid>

      <Grid size={{ xs: 12, sm: 4, md: 2 }}>
        <StatCard
          title="Open Escalations"
          icon={<ReportProblemIcon sx={{ color: 'error.main', fontSize: ICON_SIZE.xs }} />}
          iconColor={theme.palette.error.main}
          value={openEscalationsCount}
          valueColor="error.main"
          caption="Owned by or escalated to you"
        />
      </Grid>
    </Grid>
  );
}
